import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  CheckCircle2,
  QrCode,
  ShieldCheck,
  Users,
  PenLine,
  Share2,
  Clock,
  Camera,
  Loader2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiErrorDetail } from "@/lib/api";

const HERO_IMG =
  "https://images.pexels.com/photos/36494223/pexels-photo-36494223.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function relativeTime(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "pochi secondi fa";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ore fa`;
  return `${Math.floor(diff / 86400)} giorni fa`;
}

export default function PublicPetition() {
  const [petition, setPetition] = useState(null);
  const [recent, setRecent] = useState([]);
  const [form, setForm] = useState({ first_name: "", last_name: "", city: "", email: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [docPath, setDocPath] = useState("");
  const [docName, setDocName] = useState("");
  const [docUploading, setDocUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const [p, r] = await Promise.all([api.get("/petition"), api.get("/signatures/recent")]);
      setPetition(p.data);
      setRecent(r.data);
    } catch (e) {
      toast.error("Impossibile caricare la petizione");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocPath(data.document_path);
      setDocName(file.name || "documento");
      toast.success("Documento caricato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDocUploading(false);
    }
  };

  const removeDocument = () => {
    setDocPath("");
    setDocName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Accetta l'informativa sulla privacy per firmare");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        city: form.city,
        consent: form.consent,
      };
      if (form.email && form.email.trim()) payload.email = form.email.trim();
      if (docPath) payload.document_path = docPath;
      const { data } = await api.post("/signatures", payload);
      setSigned(true);
      setPetition((prev) => ({ ...prev, total_signatures: data.total_signatures }));
      load();
      toast.success("Grazie! La tua firma \u00e8 stata registrata.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const share = async () => {
    const url = "https://qr-signatures.emergent.host";
    try {
      if (navigator.share) await navigator.share({ title: petition?.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiato negli appunti");
      }
    } catch {
      /* dismissed */
    }
  };

  if (!petition)
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Caricamento...
      </div>
    );

  return (
    <div className="min-h-screen bg-background" data-testid="public-petition-page">
      {/* Header */}
      <header
        data-testid="public-petition-header"
        className="sticky top-0 z-30 backdrop-blur-xl bg-background/80 border-b border-border"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground">
              <PenLine className="w-5 h-5" />
            </div>
            <span className="font-heading font-extrabold text-lg tracking-tight">VoceCivica</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              data-testid="signature-count-badge"
              className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-primary bg-primary/10 px-3 py-1.5 rounded-full"
            >
              <Users className="w-4 h-4" />
              {petition.total_signatures} firme
            </span>
            <Button
              data-testid="share-qr-button"
              variant="outline"
              size="sm"
              className="rounded-xl gap-1.5"
              onClick={share}
            >
              <Share2 className="w-4 h-4" /> Condividi
            </Button>
            <Link to="/qr">
              <Button data-testid="open-qr-button" size="sm" className="rounded-xl gap-1.5">
                <QrCode className="w-4 h-4" /> QR Code
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 lg:py-14 grid lg:grid-cols-5 gap-8 lg:gap-12">
        {/* Left: content */}
        <div className="lg:col-span-3">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#92400E] bg-[#FEF3C7] px-3 py-1.5 rounded-full">
              <ShieldCheck className="w-4 h-4" /> Petizione Civica
            </span>
            <h1
              data-testid="petition-title"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight mt-4 text-foreground"
            >
              {petition.title}
            </h1>
            {petition.subtitle && (
              <p className="text-lg text-muted-foreground mt-3 leading-relaxed">{petition.subtitle}</p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-6 rounded-2xl overflow-hidden border border-border shadow-sm"
          >
            <img src={HERO_IMG} alt="Comunit\u00e0" className="w-full h-56 sm:h-72 object-cover" />
          </motion.div>

          {/* Signature counter */}
          <div className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <span className="font-heading text-3xl font-extrabold text-primary" data-testid="signature-total">
                {petition.total_signatures.toLocaleString("it-IT")}
              </span>
              <p className="text-sm text-muted-foreground -mt-0.5">
                {petition.total_signatures === 1 ? "firma raccolta finora" : "firme raccolte finora"}
              </p>
            </div>
          </div>

          {/* Body text */}
          <div
            data-testid="petition-body"
            className="mt-8 text-base leading-relaxed text-foreground/90 whitespace-pre-line"
          >
            {petition.body}
          </div>

          {/* Recent signatures */}
          {recent.length > 0 && (
            <div className="mt-10">
              <h3 className="font-heading text-xl font-bold mb-4">Ultime firme</h3>
              <div className="space-y-2" data-testid="recent-signatures-list">
                {recent.map((s, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-2.5"
                  >
                    <div className="w-8 h-8 rounded-full bg-accent/10 text-accent flex items-center justify-center font-semibold text-sm">
                      {s.first_name[0]}
                    </div>
                    <span className="font-medium text-sm">
                      {s.first_name} {s.last_initial}
                    </span>
                    <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" /> {relativeTime(s.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: sign form */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-24">
            {signed ? (
              <motion.div
                data-testid="signature-success-card"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-2xl border-2 border-primary/30 bg-[#D1FAE5] p-8 text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.1 }}
                  className="w-16 h-16 rounded-full bg-primary mx-auto flex items-center justify-center"
                >
                  <CheckCircle2 className="w-9 h-9 text-primary-foreground" />
                </motion.div>
                <h3 className="font-heading text-2xl font-extrabold text-[#065F46] mt-4">
                  Firma registrata!
                </h3>
                <p className="text-[#065F46]/80 mt-2">
                  Grazie per aver dato la tua voce. Condividi la petizione per raggiungere l'obiettivo.
                </p>
                <Button
                  data-testid="success-share-button"
                  onClick={share}
                  className="rounded-xl mt-6 w-full gap-2"
                >
                  <Share2 className="w-4 h-4" /> Condividi la petizione
                </Button>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="signature-form"
                className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-lg"
              >
                <h2 className="font-heading text-2xl font-bold tracking-tight">Firma ora</h2>
                <p className="text-sm text-muted-foreground mt-1 mb-5">
                  Bastano 10 secondi. La tua firma conta.
                </p>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="first_name">Nome</Label>
                    <Input
                      id="first_name"
                      data-testid="petition-first-name-input"
                      className="mt-1.5 rounded-xl"
                      value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Cognome</Label>
                    <Input
                      id="last_name"
                      data-testid="petition-last-name-input"
                      className="mt-1.5 rounded-xl"
                      value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="city" translate="no" className="notranslate">Comune di residenza</Label>
                    <Input
                      id="city"
                      autoComplete="address-level2"
                      data-testid="petition-city-input"
                      className="mt-1.5 rounded-xl"
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">
                      Email <span className="text-muted-foreground font-normal">(facoltativa)</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="petition-email-input"
                      className="mt-1.5 rounded-xl"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Documento d'identità <span className="text-muted-foreground font-normal">(facoltativo)</span></Label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,application/pdf"
                      capture="environment"
                      className="hidden"
                      data-testid="document-file-input"
                      onChange={handleDocument}
                    />
                    {!docPath ? (
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="capture-document-button"
                        disabled={docUploading}
                        onClick={() => fileInputRef.current?.click()}
                        className="mt-1.5 w-full rounded-xl h-11 gap-2 border-dashed"
                      >
                        {docUploading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
                          </>
                        ) : (
                          <>
                            <Camera className="w-4 h-4" /> Fotografa il documento
                          </>
                        )}
                      </Button>
                    ) : (
                      <div
                        data-testid="document-uploaded-chip"
                        className="mt-1.5 flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5"
                      >
                        <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                        <span className="text-sm truncate flex-1">{docName}</span>
                        <button
                          type="button"
                          data-testid="remove-document-button"
                          onClick={removeDocument}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-start gap-3 pt-1">
                    <Checkbox
                      id="consent"
                      data-testid="privacy-checkbox"
                      checked={form.consent}
                      onCheckedChange={(v) => setForm({ ...form, consent: !!v })}
                    />
                    <Label htmlFor="consent" className="text-sm font-normal leading-snug text-muted-foreground">
                      Acconsento al trattamento dei miei dati personali ai sensi del GDPR per la sola
                      raccolta firme di questa petizione.
                    </Label>
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="sign-petition-submit-button"
                  disabled={submitting}
                  className="w-full rounded-xl mt-6 h-12 text-base font-semibold gap-2"
                >
                  <PenLine className="w-4 h-4" />
                  {submitting ? "Invio..." : "Firma la Petizione"}
                </Button>
              </form>
            )}

            <Link to="/admin/login">
              <p
                data-testid="admin-link"
                className="text-center text-xs text-muted-foreground mt-4 hover:text-foreground transition-colors cursor-pointer"
              >
                Area organizzatori
              </p>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
